import { Box, styled, Typography, useTheme } from '@mui/material'
import EidTableGrid from 'components/EidTableGrid/EidTableGrid'
import {
    useAllCarts,
    useApplication,
    useApplicationAppRightsAssignments,
} from 'hooks'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Filters } from 'components'
import AssigneePerson from 'components/AssigneePerson/AssigneePerson'
import useSubcomponents from 'useSubcomponents'
import { useHistory } from 'react-router'
import { ApplicationTypes } from 'utils'
import { PageContextProvider } from 'pageContext'
import ApplicationRoleDefinitionDrawer from '../ApplicationRoleDefinitionAssignments/ApplicationRoleDefinitionDetails'
import DeleteApplicationRoleRightAssignment from '../ApplicationRoleDefinitionAssignments/DeleteRoleRightAssignment'
import { getFullPathFromUrl, useQuery } from 'packages/core'
import ButtonWithDropdown from 'components/ButtonWithDropdown'
import config from 'config'
import EditAssignmentsWithFieldTypes from 'components/Popups/EditAssignmentsWithFieldTypes'
import DialogHeader from 'components/Popups/EditAssignmentsWithFieldTypes/DialogHeader'
import FieldTypeJsonPreviewButton from 'components/FieldTypeJsonPreview'
import moment from 'moment'

const TableOverView = styled(Box)({
    padding: '16px 0px',
    '& table': {
        boxShadow: '0 4px 17px 0 rgba(0, 0, 0, 0.07)',
    },
})
const SearchMenuBarFilter = styled(Box)({
    width: '100%',

    '& svg': {
        color: ' #aab0b4',
        marginRight: '3px',
    },
    '& div:nth-child(1)': {
        boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1) !important',
        border: 'solid 1px #ebebed',
        paddingLeft: '0',
        height: '40px',
    },
    '& ::placeholder': {
        color: '#aab0b4',
    },
})

const ButtonsBox = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
})

const ApplicationAppRightsAssignments = ({
    resourceSystemModuleID,
    resourceId,
    applicationType,
}) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const query = useQuery()
    const history = useHistory()
    const { data: carts } = useAllCarts()
    const appRightId = query.get('rightId')
    const applicationId = query.get('applicationId')
    const { data: application } = useApplication(applicationId)
    const { list, loading, loadingMore, pagination } =
        useApplicationAppRightsAssignments(
            `/api/LocalRights/appRoles/membersByApplicationID`,
            resourceSystemModuleID,
        )
    const [assignee, setAssignee] = useState(null)

    const {
        canAddApplicationAppRightAssignments,
        getAccessBasedAttributes,
        hasAccessToControl,
    } = useSubcomponents()

    const attributes = [
        {
            name: 'assignee',
            sortable: true,
            label: 'Assignee',
            hideValue: true,
            component: {
                componentName: 'renderer',
                render: (data) => {
                    return (
                        <AssigneePerson
                            assigneeName={data.assignee}
                            imageUrl={data.personImageThumbUrl}
                            assigneeType={data.value}
                        />
                    )
                },
            },
            //NOTE : Keeping the commented out code for subcomponent traceability
            // requireAccess: {
            //     control:
            //         'ResourceAdmin-ApplicationDetails-PbacAssignments-AppRights-Assignee',
            // },
        },
        {
            name: 'appRightFriendlyName',
            sortable: true,
            label: 'AppRight',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-PbacAssignments-AppRights-AppRight',
            },
        },
        {
            name: 'rightType',
            sortable: true,
            label: 'RightType',

            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-PbacAssignments-AppRights-RightType',
            },
        },
        {
            name: 'assignedTo',
            sortable: true,
            label: 'AssignedTo',

            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-PbacAssignments-AppRights-AssignedTo',
            },
        },
        {
            name: 'application',
            sortable: true,
            label: 'Application',

            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-PbacAssignments-AppRights-Application',
            },
        },

        {
            name: 'resourceSystem',
            sortable: true,
            label: 'ResourceSystem',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-PbacAssignments-AppRights-ResourceSystem',
            },
        },
        {
            name: 'startDate',
            sortable: true,
            label: 'StartDate',
            hideValue: true,
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-PbacAssignments-AppRights-StartDate',
            },
            component: {
                componentName: 'renderer',
                render: (data) => {
                    return (
                        <Typography sx={{ fontSize: '1.3rem' }}>
                            {data.startDate
                                ? moment
                                      .utc(data.startDate)
                                      .local()
                                      .format('L LT')
                                : '-'}
                        </Typography>
                    )
                },
            },
        },
        {
            name: 'endDate',
            sortable: true,
            label: 'EndDate',
            hideValue: true,
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-PbacAssignments-AppRights-EndDate',
            },
            component: {
                componentName: 'renderer',
                render: (data) => {
                    return (
                        <Typography sx={{ fontSize: '1.3rem' }}>
                            {data.endDate
                                ? moment
                                      .utc(data.endDate)
                                      .local()
                                      .format('L LT')
                                : '-'}
                        </Typography>
                    )
                },
            },
        },
        {
            hideValue: true,
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-PbacAssignments-AppRights-Action',
            },
            component: {
                componentName: 'renderer',
                render: (data) => {
                    return (
                        <ButtonsBox>
                            <FieldTypeJsonPreviewButton data={data} />
                            <Box>
                                {(applicationType ===
                                    ApplicationTypes.PbacHasAppResourcesWithFieldType ||
                                    applicationType ===
                                        ApplicationTypes.PBACHasNoAppResourceWithFieldType ||
                                    applicationType ===
                                        ApplicationTypes.AzureAppWithPbac) && (
                                    <Button
                                        onClick={() => {
                                            query.set('rightId', data?.id)
                                            history.push(
                                                `${
                                                    history.location.pathname
                                                }?${query.toString()}`,
                                            )
                                        }}
                                        style={{
                                            borderRadius: '4px',
                                            backgroundColor:
                                                theme?.palette?.primary?.main,
                                            color: '#ffffff',
                                            fontSize: '14px',
                                            height: '100%',
                                            '&:hover': {
                                                backgroundColor:
                                                    theme?.palette?.primary
                                                        ?.main,
                                            },
                                        }}
                                    >
                                        <Box style={{ display: 'flex' }}>
                                            {t('Edit')}
                                        </Box>
                                    </Button>
                                )}
                            </Box>
                            <DeleteApplicationRoleRightAssignment
                                item={data}
                                carts={carts}
                                application={application}
                                keyForAssignee={'appRightFriendlyName'}
                                keyForRoleRightId={'localRightId'}
                            />
                        </ButtonsBox>
                    )
                },
            },
        },
    ]
    const [showAssignmentModal, setShowAssignmentModal] = useState(false)
    const assignRightsDropdownOptions = [
        {
            label: 'AssignToPerson',
            onClick: () => {
                setShowAssignmentModal(true)
            },
            display: true,
            requireAccess: {
                control:
                    'ResourceAdmin-Applications-AppRightsAssignment-AssignToPerson',
            },
        },
        {
            label: 'AssignRightToAnyOtherAssigneeType',
            display: true,
            onClick: () => {
                handleAssignRightByWf()
            },
            requireAccess: {
                control:
                    'ResourceAdmin-Applications-AppRightsAssignment-AssignToAnyOtherAssigneeType',
            },
        },
    ]
    const handleAssignRightByWf = () => {
        query.set('workflowName', 'AssignAzLocalRightScope')
        query.set('returnUrl', getFullPathFromUrl())

        if (resourceId) {
            query.set('SelectedResourceID', `${resourceId}`)
        }
        history.push(
            `${config.APP_SUBPATH}/applications/workflows?${query.toString()}`,
        )
    }
    const getOptionsToRender = () => {
        return assignRightsDropdownOptions.filter(
            (d) => d.display && hasAccessToControl(d?.requireAccess?.control),
        )
    }
    return (
        <Fragment>
            <Box>
                <Box
                    width="100%"
                    display="flex"
                    alignItems="space-between"
                    justifyContent="space-between"
                >
                    {canAddApplicationAppRightAssignments && (
                        <Box
                            data-protectedsubcomponent={
                                canAddApplicationAppRightAssignments
                            }
                        >
                            {getOptionsToRender().length > 0 && (
                                <ButtonWithDropdown
                                    label={'AssignAppRight'}
                                    options={getOptionsToRender()}
                                />
                            )}
                        </Box>
                    )}

                    <Box
                        width="240px"
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                    >
                        <SearchMenuBarFilter>
                            <Filters.TextSearch outlined />
                        </SearchMenuBarFilter>
                    </Box>
                </Box>
                <TableOverView>
                    <EidTableGrid
                        rowSelection={false}
                        selectAllAction={null}
                        noDataMessage={t('NoDataMessage')}
                        pagination={pagination}
                        isLoading={loading}
                        isLoadingMore={loadingMore}
                        attributes={getAccessBasedAttributes(attributes)}
                        list={list}
                    />
                </TableOverView>
            </Box>
            {appRightId && list?.length && (
                <PageContextProvider key="RequestViewAppRights">
                    <ApplicationRoleDefinitionDrawer
                        assigneePerson={list.find((x) => x.id === appRightId)}
                        keyForAssignee={'appRightFriendlyName'}
                        keyForRoleRightId={'localRightId'}
                        isRole={false}
                        onClose={() => {
                            query.delete('rightId')
                            history.push(
                                `${
                                    history.location.pathname
                                }?${query.toString()}`,
                            )
                        }}
                    />
                </PageContextProvider>
            )}
            <PageContextProvider
                key={'ApplicationRightsPeopleEditAssignmentWithFieldTypes'}
            >
                <DialogHeader
                    headerText={{
                        dynamicText: application?.friendlyName,
                        text: 'AssignRights',
                    }}
                    setShowAssignmentModal={setShowAssignmentModal}
                    showAssignmentModal={showAssignmentModal}
                    assigneePerson={assignee}
                    setAssigneePerson={setAssignee}
                    personSelectorLabelKey={'SelectPersonToAssignRights'}
                    personSelectorUrl={`api/people/allPeopleByResource`}
                    personSelectorQueryParams={{
                        resourceId: applicationId,
                        showLocalRights: true,
                    }}
                    showPersonSelector={true}
                >
                    {assignee && (
                        <EditAssignmentsWithFieldTypes
                            resourceType={'Applications'}
                            assignedUrl={`api/LocalRights/appRoles/membersByApplicationID`}
                            unAssignedUrl={`api/LocalRights/GetEligibleAppRightsByApplicationByAssignee`}
                            targetAssigneeId={applicationId}
                            searchPlaceHolder="SearchRights"
                            assignedQueryParams={{
                                resourceSystemModuleID: resourceSystemModuleID,
                                assigneeId: assignee?.id,
                            }}
                            unAssignedQueryParams={{
                                applicationId,
                                eligibilityAssigneeId: assignee?.id,
                            }}
                            showAccessRequestPolicy={true}
                            fetchRequestPolicyItemWise={true}
                            showStartDate={false}
                            fieldTypeProps={{
                                assignee: assignee,
                                isRole: false,
                                keyForRoleRightId: 'localRightId',
                            }}
                            requireRemappingInAssignedList={true}
                            newMappingForAssignedList={{
                                friendlyName: 'appRightFriendlyName',
                                startDateUtc: 'startDate',
                                endDateUtc: 'endDate',
                            }}
                            showAssignmentModal={showAssignmentModal}
                            handleClose={() => setShowAssignmentModal(false)}
                        />
                    )}
                </DialogHeader>
            </PageContextProvider>
        </Fragment>
    )
}

export default ApplicationAppRightsAssignments
